import React from "react";
import { Avatar, Box, Button, Link, List, ListItem, Paper, Tooltip, Typography, useTheme } from "@mui/material";
import { AIMetadata, AISummaryOutput } from "system/AIResponseTypes";
import { Query } from "system/query/Query";
import { useTranslation } from "react-i18next";
import SentimentColor from "components/design/SentimentColor";
import RoundedNumber from "components/formatting/RoundedNumber";
import AIMarkdown from "components/design/AIMarkdown";
import TopicsTooltip from "components/widgets/charts/parts/TopicsTooltip";
import ISONumber from "components/formatting/ISONumber";
import ISODateTime from "components/formatting/ISODateTime";
import AISummarySparkline from "./AISummarySparkline";

export interface AISummaryRendererProps {
  data: AISummaryOutput;
  metadata: AIMetadata;

  setDrilldownQuery: (query: Query | undefined) => void;
  setDrilldownTitle: (title: string | undefined) => void;

  exportContentRef?: React.RefObject<HTMLDivElement> | null;
}

export function AISummaryRenderer(props: AISummaryRendererProps) {
  const theme = useTheme();
  const { i18n } = useTranslation();

  const { data, metadata, setDrilldownQuery, setDrilldownTitle } = props;

  const [hoveredTerm, setHoveredTerm] = React.useState<any | null>(null);

  const narratives = data?.narratives?.map((n, index:number) => {
    // Sometimes the AI thinks examples are key_terms? Filter out any examples that are very short
    let examples:Array<string> = [];
    if(n?.examples && n.examples.length > 0) {
      examples = n.examples.filter((v: string) => {return v.length > 8 && v.split(' ').length > 2})
    }
    const hasPositivity = n.positivity !== undefined && n.positivity !== null;

    return (
      <Paper key={index} sx={{ p: 2, mb: 2, breakBefore: "page" }}>
        {/* Title */}
        <Box sx={{ display:"flex"}}>
          <Avatar>{index+1}</Avatar>
          {n?.query &&
            <Link 
              sx={{cursor: "pointer"}} 
              onClick={() => {
                setDrilldownQuery(new Query({ query: n?.query }));
                setDrilldownTitle(n?.title || "");
            }}>
              <Typography variant='h3' sx={{pb:1, pl:2}} color="primary">
                {n?.title || ""}
              </Typography>
            </Link>
          }
          {!n?.query &&
            <Typography variant='h3'>
              {n?.title || ""}
            </Typography>
          }
        </Box>

        {/* Stats */}
        <List sx={{ display: "flex", justifyContent: "space-evenly", flexWrap: "wrap", p: 0 }}>
          {hasPositivity &&
            <ListItem key={`${index}-pos`} sx={{ display: "flex", justifyContent: "center", width: "auto" }}>
              <SentimentColor positivity={n.positivity ?? 0}>
                <Typography variant='h4'>{((n.positivity ?? 0) * 100).toFixed(0)}%</Typography>
              </SentimentColor>
              <Typography variant='body1' sx={{ ml: 1 }}>{i18n.t("positivity")}</Typography>
            </ListItem>
          }
          {metadata?.total_count && n?.distribution &&
            <ListItem key={`${index}-cnt`} sx={{ display: "flex", justifyContent: "center", width: "auto" }}>
              {n.distribution * metadata.total_count < 1000 && '~'}
              <RoundedNumber number={n.distribution * metadata.total_count} variant='h4' />
              <Typography variant='inherit' sx={{ ml: 1 }}>{i18n.t("records")}</Typography>
            </ListItem>
          }
          {metadata?.total_count && n?.distribution &&
            <ListItem key={`${index}-dist`} sx={{ display: "flex", justifyContent: "center", width: "auto" }}>
              <Typography variant='h4' color="primary">{(n.distribution * 100).toFixed(n.distribution < 0.02 ? 1 : 0)}%</Typography>
              <Typography variant='inherit' sx={{ ml: 1 }}>{i18n.t("distribution")}</Typography>
            </ListItem>
          }
        </List>

        {/* Terms */}
        <List sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", py: 0, px: 0 }}>
          {n?.key_terms && n?.key_terms.map((t, tidx:number) => {
            return <ListItem key={`${index}-${tidx}`} sx={{ display: "flex", alignItems: "stretch", width: "auto", pl: 0, pr: 1, py: 0.5 }}>
              <Button
                sx={{ px: 1, py: 0.5 }}
                variant='outlined' 
                onClick={() => {
                  setDrilldownQuery(new Query({ query: t.query }));
                  setDrilldownTitle(t.label);
                }}
                onMouseEnter={() => { setHoveredTerm(t) }}
                onMouseLeave={() => { setHoveredTerm(null) }}
              >
                {t.label}
              </Button>
            </ListItem>
          })}
        </List>

        {/* Body */}
        <Box sx={{width: "100%" }}>
          <AIMarkdown markdownContent={n?.description || ""} />
        </Box>

        {/* Examples */}
        {examples.length > 0 &&
          <Box>
            <Typography variant='h4' sx={{color: theme.palette.grey[500], pb: 1}}>
              {i18n.t("widget-types.ai-summary.example-posts")}
            </Typography>
            {examples.map((e, eidx:number) => {
              return <Paper key={`e-${index}-${eidx}`} sx={{
                p: 2,
                mb: 1,
                color: theme.palette.text.secondary, 
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: "none" }}>
                  {e}
              </Paper>
            })}
          </Box>
        }
      </Paper>
    );
  });

  let showTimesOnDates = true;
  if(metadata?.min_timestamp && metadata?.max_timestamp) {
    const minT = new Date(metadata.max_timestamp);
    const maxT = new Date(metadata.min_timestamp);
    showTimesOnDates = (maxT - minT) >= 7*86400*1000;
  }

  return (
    <Tooltip
      title={hoveredTerm ? <TopicsTooltip topic={hoveredTerm} withCount={false} /> : ''}
      open={Boolean(hoveredTerm)}
      followCursor={true}
    >
      <Box ref={props.exportContentRef}>
        <Box>
          {data?.title &&
            <Typography variant="h3" sx={{pb:2}}>{data.title}</Typography>
          }
          <List sx={{ display: "flex", width: "100%", justifyContent: "space-evenly", flexWrap: "wrap", p:0, pb:1 }}>
            {metadata?.positivity !== null && metadata?.positivity !== undefined &&
              <ListItem key={'overall-pos'} sx={{display: "flex", justifyContent: "center", width: "auto"}}>
                <SentimentColor positivity={metadata.positivity}>
                  <Typography variant='h4'>{(metadata.positivity * 100).toFixed(0)}%</Typography>
                </SentimentColor>
                  <Typography variant='inherit' sx={{ ml: 1 }}>{i18n.t("overall")} {i18n.t("positivity")}</Typography>
              </ListItem>
            }
            {metadata?.total_count &&
              <ListItem key={'overall-count'} sx={{display: "flex", justifyContent: "center", width: "auto"}}>
                <ISONumber number={metadata.total_count} typographyVariant='h4' />
                <Typography variant='inherit' sx={{ ml: 1 }}>{i18n.t("records")} {i18n.t("analyzed")}</Typography>
              </ListItem>
            }
            {metadata?.min_timestamp && metadata?.max_timestamp &&
              <ListItem key={'overall-timerange'} sx={{display: "flex", justifyContent: "center", width: "auto"}}>
                <ISODateTime isoString={metadata.min_timestamp} hideTime={!showTimesOnDates} sx={{ mr: 1 }}/>
                -
                <ISODateTime isoString={metadata.max_timestamp} hideTime={!showTimesOnDates} sx={{ ml: 1 }}/>
              </ListItem>
            }
          </List>
          {data?.answer && metadata?.query_prompt &&
            <Paper key={'ai-summary-structured-answer'} sx={{p: 2, mb: 3, pb: 1}}>
              <Typography variant="h4" sx={{p: 0}}>{metadata.query_prompt}</Typography>
              <AIMarkdown markdownContent={data.answer} />
            </Paper>
          }
          {data?.introduction &&
            <AIMarkdown markdownContent={data.introduction || ""} />
          }
        </Box>
        {narratives && narratives.length &&
          <Box>
            <Typography variant="h3" sx={{ px: 0, py: 1 }}>{i18n.t("narratives")}</Typography>
            <AISummarySparkline data={data} metadata={metadata} />
            {narratives}
          </Box>
        }
        {data?.conclusion &&
          <Box sx={{ breakBefore: "page" }}>
            <Typography variant="h3" sx={{ px: 0, py: 1}}>{i18n.t("conclusion")}</Typography>
            <AIMarkdown markdownContent={data.conclusion || ""} />
          </Box>
        }
      </Box>
    </Tooltip>
  );
}